import React, { useEffect, useState } from 'react';
import 'datatables.net-dt/css/dataTables.dataTables.min.css';
import "datatables.net";
import { useNavigate } from 'react-router-dom';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;
const limit = 10;
const $ = window.$;

const Product = () => {
    const navigate = useNavigate();
    const brandIdRef = React.useRef('');
    const platformRef = React.useRef('');
    const [affiliatePlatform, setAffiliatePlatform] = useState('');
    const [brandList, setBrandList] = useState([]);
    const [selectedBrandId, setSelectedBrandId] = useState('');
    const lastEndCursorRef = React.useRef('');
    const lastStartCursorRef = React.useRef('');
    const cursorStackRef = React.useRef([]);
    const currentPageRef = React.useRef(0);

    const handlePlatformChange = async (e) => {
        const selected = e.target.value;
        setAffiliatePlatform(selected);
        platformRef.current = selected;
        setSelectedBrandId('');
        brandIdRef.current = '';

        if (selected === 'CJ' || selected === 'IMPACT') {
            try {
                const res = await authManager.authenticatedFetch(
                    `${Base_Url}/shopping/brands/admin/list?network=${selected}&page=1&limit=500`
                );
                const json = await res.json();
                const brands = json?.result?.brands || [];
                setBrandList(brands);
            } catch (err) {
                console.error('Failed to fetch brands:', err);
                setBrandList([]);
            }
        }
    };

    const handleBrandChange = (e) => {
        const brandId = e.target.value;
        setSelectedBrandId(brandId);
        brandIdRef.current = brandId;
    };

    useEffect(() => {
        const table = document.getElementById('products');
        if (!table) return;

        const handleClick = (e) => {
            const target = e.target.closest('[data-navigate]');
            if (target) {
                navigate(target.getAttribute('data-navigate'));
            }
        };

        table.addEventListener('click', handleClick);
        return () => table.removeEventListener('click', handleClick);
    }, [navigate]);

    useEffect(() => {
        let table;

        const initTable = () => {
            if ($.fn.dataTable.isDataTable('#products')) {
                $('#products').DataTable().destroy();
            }

            table = $('#products').DataTable({
                serverSide: true,
                processing: true,
                ordering: true,
                searching: true,
                pageLength: limit,
                lengthChange: false,
                deferRender: true,
                pagingType: 'simple',

                ajax: async function (data, callback) {
                    const search = data.search.value;
                    const draw = data.draw;
                    const sortColumnIndex = data.order[0]?.column;
                    const sortDirection = data.order[0]?.dir || 'asc';

                    const columnFieldMap = {
                        2: 'title',
                        3: 'brandId.name',
                        5: 'price.amount'
                    };

                    const sortBy = columnFieldMap[sortColumnIndex] || 'createdAt';
                    const sortParams = `&sortBy=${encodeURIComponent(sortBy)}&sortOrder=${sortDirection}`;

                    try {
                        const brandId = brandIdRef.current;
                        const platform = platformRef.current;

                        const requestedPage = Math.floor(data.start / data.length);
                        const currentPage = currentPageRef.current;
                        const isSearching = search?.trim().length > 0;
                        const isSortingChanged = sortBy !== 'createdAt' || sortDirection !== 'asc';

                        let paginationParam = `first=${limit}&status=active`;

                        if (!isSearching && !isSortingChanged) {
                            if (requestedPage > currentPage) {
                                if (lastEndCursorRef.current) {
                                    paginationParam = `first=${limit}&after=${lastEndCursorRef.current}`;
                                    currentPageRef.current = requestedPage;
                                }
                            } else if (requestedPage < currentPage) {
                                const currentCursor = cursorStackRef.current[currentPage];
                                if (currentCursor?.startCursor) {
                                    paginationParam = `last=${limit}&before=${currentCursor.startCursor}`;
                                    currentPageRef.current = requestedPage;
                                }
                            }
                        } else {
                            currentPageRef.current = 0;
                            cursorStackRef.current = {};
                        }

                        const url = `${Base_Url}/shopping/products/admin/list-graphql?${paginationParam}&search=${search}${sortParams}`;
                        const fullUrl = `${url}${brandId ? `&brandId=${brandId}` : ''}${platform ? `&network=${platform}` : ''}`;

                        const response = await authManager.authenticatedFetch(fullUrl);
                        const dataJson = await response.json();
                        const res = dataJson?.data;

                        lastEndCursorRef.current = res?.pageInfo?.endCursor;
                        lastStartCursorRef.current = res?.pageInfo?.startCursor;

                        cursorStackRef.current[currentPageRef.current] = {
                            startCursor: res?.pageInfo?.startCursor,
                            endCursor: res?.pageInfo?.endCursor
                        };

                        const products = (res?.edges || []).map(edge => edge.node);
                        const totalItems = res?.totalCount;


                        const transformed = products.map((product, index) => ({
                            serial: data.start + index + 1,
                            image: product.productLogo || '/assets/img/product/no_product.png',
                            name: product.title || '-',
                            brand: product.brand?.name || '-',
                            AffiliatePlatforms: product.brand?.network || '-',
                            price: `$${product.price?.amount?.toFixed(2) || '0.00'}`,
                            rating: product.rating !== null ? product.rating : 'N/A',
                            _id: product._id
                        }));

                        callback({
                            draw: draw,
                            recordsTotal: totalItems,
                            recordsFiltered: totalItems,
                            data: transformed
                        });
                    } catch (err) {
                        console.error('Error loading products:', err);
                        callback({
                            draw: draw,
                            recordsTotal: 0,
                            recordsFiltered: 0,
                            data: []
                        });
                    }
                },
                columns: [
                    { data: 'serial', title: 'S.No', className: 'text-center', orderable: false, width: '60px' },
                    {
                        data: 'image',
                        orderable: false,
                        className: 'text-center',
                        render: (data) =>
                            `<div class="prodlist_img"><img src="${data}" alt="product" border="0" /></div>`,
                    },
                    { data: 'name', title: 'Name', orderable: true, width: '30%' },
                    { data: 'brand', title: 'Brand', orderable: true, width: '20%' },
                    { data: 'AffiliatePlatforms', title: 'Affiliate Platforms', orderable: false, width: '20%' },
                    { data: 'price', title: 'Price', orderable: true, width: '15%' },
                    {
                        data: 'rating',
                        title: 'Rating',
                        orderable: false,
                        width: '10%',
                        render: (rating) => {
                            if (!rating || rating === 'N/A') return 'N/A';
                            const stars = Math.round(rating);
                            return `
                                <div class="tp-product-rating-icon">
                                    ${'<span><i class="fa-solid fa-star"></i></span>'.repeat(stars)}
                                </div>
                            `;
                        }
                    },
                    {
                        data: '_id',
                        title: 'Action',
                        className: 'text-center',
                        width: '25%',
                        orderable: false,
                        render: id => `
                            <div align="center">
                                <i class="fa-solid fa-eye mr-2 crsr_pntr action-icon" data-navigate="/products/${id}"></i>
                                
                            </div>`
                    }
                ],
                drawCallback: function () {
                    const tablePosition = document.getElementById('products')?.offsetTop;
                    if (tablePosition !== undefined) {
                        window.scrollTo({ top: tablePosition - 100, behavior: 'smooth' });
                    }
                }
            });
        };

        const timeout = setTimeout(initTable, 100);

        return () => {
            clearTimeout(timeout);
            if (table) table.destroy(true);
        };
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        lastEndCursorRef.current = '';
        lastStartCursorRef.current = '';
        cursorStackRef.current = [];
        currentPageRef.current = 0;
        $('#products').DataTable().ajax.reload();
    };
    return (
        <div>
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Products</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="frmwdth_cntr_640">
                                <div className="col-lg-12">
                                    <div className="card card-primary card-outline">
                                        <form className="form-horizontal" onSubmit={handleSearch}>
                                            <div className="card-body">
                                                <div className="form-group row">
                                                    <label className="col-12 col-md-4 col-form-label">
                                                        Affiliate Platforms
                                                    </label>
                                                    <div className="col-12 col-md-8 mb-3 mb-md-0">
                                                        <select className="form-control" value={affiliatePlatform} onChange={handlePlatformChange}>
                                                            <option value="">Select</option>
                                                            <option value="CJ">CJ</option>
                                                            <option value="IMPACT">Impact</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-4 col-form-label">Brands</label>
                                                    <div className="col-12 col-md-8 mb-3 mb-md-0">
                                                        <select className="form-control" value={selectedBrandId} onChange={handleBrandChange}>
                                                            <option value="">Select</option>
                                                            {brandList.map((brand) => (
                                                                <option key={brand._id} value={brand._id}>
                                                                    {brand.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="card-footer text-center">
                                                <button type="submit" className="btn btn-blue">
                                                    Search
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-12">
                            <div className="table-responsive">
                                <table id="products" width="100%" className="table table-striped table-bordered txt_lft">
                                    <thead>
                                        <tr>
                                            <th>S.No</th>
                                            <th>Image</th>
                                            <th>Name</th>
                                            <th>Brand</th>
                                            <th>Affiliate Platforms</th>
                                            <th>Price</th>
                                            <th>Rating</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody></tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Product;
